import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";
import { showErrorSnackbar, type ErrorSnackbarContext } from "_shared/_errors/error-snackbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import {
  DEFAULT_VIEW_NAME,
  columnChoicesDiffer,
  columnLayoutsDiffer,
  isSameListView,
  type ColumnLayoutChoice,
  type ListViewPath,
} from "_shared/_views/list-pages/current-table-view";
import { ManageViewsDialog } from "_shared/_views/list-pages/manage-views-dialog";
import { SaveViewDialog } from "_shared/_views/list-pages/save-view-dialog";
import type { CurrentTableView } from "_shared/_views/list-pages/use-current-table-view";
import { fromColumnVisibilityEntries } from "_shared/_views/tables/_api/table-column-preferences-api";
import {
  fromTableViewStateJSON,
  useCreateTableViewMutation,
  useUpdateTableViewMutation,
  type TableViewState,
} from "_shared/_views/tables/_api/table-views-api";
import type {
  NamedTableViewJSON,
  TableColumnPreferenceJSON,
} from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import { ReaderColumnChoiceContext } from "_shared/_views/tables/table-column-preferences-context";
import { sheetPaperSx, SheetHeading } from "_shared/_views/tables/table-sheet";
import { tableTitle } from "_shared/_views/tables/table-titles";
import { useContext, useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useSession } from "users/__session/session-context";

/** What the menu says when a view could not be saved. */
export const SAVE_VIEW_FAILURE: ErrorSnackbarContext = {
  doing: "saving your view",
  operation: "tableViews.save",
  missingMessage: "This view is no longer saved. It may have been deleted in another browser. Refresh to see your views.",
};

/** The widths and order a view keeps; none for the Default view. */
function layoutOf(state: TableViewState | undefined): ColumnLayoutChoice {
  return { columnSizing: state?.columnSizing ?? {}, columnOrder: state?.columnOrder ?? [] };
}

/** The widest the Views button draws beside a search box; on a phone it is narrower. */
export const VIEWS_BUTTON_MAX_WIDTH = 280;

/** What the menu shows about the view in use. Pure, so each state is tested on its own. */
export type ViewsMenuState = Readonly<{
  /** The saved view in use; undefined for the Default view. */
  activeView: NamedTableViewJSON | undefined;
  /** The name on the button. */
  name: string;
  /** Whether the list differs from the view in use: its tab, chips, filters, sort, columns or widths. */
  edited: boolean;
}>;

/**
 * The view in use and whether the list still shows it. A view the account no longer has -- deleted in
 * another browser -- is the Default view. Paths go through `keep` first, so a parameter the page no
 * longer reads, or one spelled another way, is not an edit.
 */
export function viewsMenuState(args: {
  views: readonly NamedTableViewJSON[];
  activeViewId: string | null;
  defaultPath: string;
  shownView: ListViewPath;
  keep?: (view: ListViewPath) => ListViewPath | undefined;
  columnChoice: Readonly<Record<string, boolean>>;
  columnIds: readonly string[];
  isShownByDefault: (columnId: string) => boolean;
  /** The widths and order the list shows, as the account's current view keeps them. */
  columnLayout: ColumnLayoutChoice;
}): ViewsMenuState {
  const activeView = args.views.find((view) => view.id === args.activeViewId);
  const saved = activeView ? fromTableViewStateJSON(activeView.state) : undefined;
  const basePath: ListViewPath = saved?.tabPath
    ? { tabPath: saved.tabPath, search: saved.search }
    : { tabPath: args.defaultPath, search: "" };
  const normalize = (view: ListViewPath) => args.keep?.(view) ?? view;
  const edited =
    !isSameListView(normalize(basePath), normalize(args.shownView)) ||
    columnChoicesDiffer(args.columnChoice, saved?.columnVisibility ?? {}, args.columnIds, args.isShownByDefault) ||
    columnLayoutsDiffer(args.columnLayout, layoutOf(saved), args.columnIds);
  return { activeView, name: activeView?.name ?? DEFAULT_VIEW_NAME, edited };
}

/**
 * The Views menu at the start of a list's toolbar (#6717). It always names the view in use -- the
 * page's Default view or one the reader saved -- and adds "· edited" once the list differs from it.
 * From it the reader switches views, saves their changes into the view or as a new one, discards
 * them, and manages their views. On a phone it opens as a bottom sheet.
 *
 * Drawn inside the table's toolbar, so it reads the reader's Show/hide columns choice from the table.
 */
export function ViewsMenu<TData extends MRT_RowData>(props: ViewsMenuProps<TData>) {
  if (!props.views.keepsViews) return null;
  return <KeptViewsMenu {...props} />;
}

type ViewsMenuProps<TData extends MRT_RowData> = Readonly<{
  views: CurrentTableView;
  table: MRT_TableInstance<TData>;
  /**
   * Which columns the page shows when the reader has chosen none, for a page that decides that
   * itself rather than through `initialState`. A column it leaves out is shown.
   */
  defaultColumnVisibility?: Readonly<Record<string, boolean>>;
}>;

/** What the list showed, and as which view, when a save was asked for. */
type ShownSnapshot = Readonly<{ activeViewId: string | null; shown: TableViewState }>;

function isSameSnapshot(left: ShownSnapshot, right: ShownSnapshot): boolean {
  return left.activeViewId === right.activeViewId && JSON.stringify(left.shown) === JSON.stringify(right.shown);
}

/** The menu for a reader who keeps views: signed in, and not shown one fixed view. */
function KeptViewsMenu<TData extends MRT_RowData>(props: ViewsMenuProps<TData>) {
  // The table instance is one object MRT edits in place; see `TableToolsMenu`.
  "use no memo";

  const { views: tableViews, table, defaultColumnVisibility } = props;
  const { views, activeViewId, defaultPath, shownView, keepWhatStillExists, tableKey, currentView } = tableViews;
  const session = useSession();
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const sheetTitleId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saveDialog, setSaveDialog] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);
  const [createView, createState] = useCreateTableViewMutation();
  const [updateView, updateState] = useUpdateTableViewMutation();
  const title = tableTitle(tableKey);

  const readerChoice = useContext(ReaderColumnChoiceContext);
  // What the list shows as of the last render, for a save that answers after the reader moved on.
  const latest = useRef<ShownSnapshot | undefined>(undefined);

  const columnChoice = readerChoice ?? fromColumnVisibilityEntries(currentView?.state.columnVisibility ?? []);
  const initialVisibility = defaultColumnVisibility ?? table.options.initialState?.columnVisibility ?? {};
  const columnIds = table.getAllLeafColumns().map((column) => column.id);
  const current = currentView ? fromTableViewStateJSON(currentView.state) : undefined;
  const currentLayout = layoutOf(current);
  const state = viewsMenuState({
    views,
    activeViewId,
    defaultPath,
    shownView,
    keep: keepWhatStillExists,
    columnChoice,
    columnIds,
    isShownByDefault: (id) => initialVisibility[id] ?? true,
    columnLayout: currentLayout,
  });
  const { activeView, name, edited } = state;

  /** What the list shows now, as a saved view keeps it. Widths and order are the current view's. */
  function shownState(): TableViewState {
    return {
      tabPath: shownView.tabPath,
      search: shownView.search,
      columnVisibility: columnChoice,
      columnSizing: currentLayout.columnSizing,
      columnOrder: currentLayout.columnOrder,
    };
  }

  useEffect(() => {
    latest.current = { activeViewId, shown: shownState() };
  });
  useEffect(
    () => () => {
      latest.current = undefined;
    },
    [],
  );

  /**
   * Whether the list still shows what was saved, as the same view. A save that answers after the reader
   * opened another view, or changed the list, saves the view but leaves the list as the reader has it
   * now: making the saved state current would undo their newer choice (#6717 review).
   */
  function stillShows(started: ShownSnapshot): boolean {
    return latest.current !== undefined && isSameSnapshot(latest.current, started);
  }

  function close() {
    setAnchor(null);
    setSheetOpen(false);
  }

  function open(event: MouseEvent<HTMLElement>) {
    if (isPhone) setSheetOpen(true);
    else setAnchor(event.currentTarget);
  }

  function choose(view: NamedTableViewJSON | null) {
    close();
    tableViews.openView(view);
  }

  async function saveChanges(view: NamedTableViewJSON) {
    close();
    if (!session) return;
    const shown = shownState();
    const started: ShownSnapshot = { activeViewId, shown };
    try {
      await updateView({ session, tableKey, viewId: view.id, name: view.name, state: shown }).unwrap();
    } catch (error) {
      showErrorSnackbar(error, SAVE_VIEW_FAILURE);
      return;
    }
    if (stillShows(started)) tableViews.keepAsCurrentView(view.id, shown);
    enqueueSnackbar(`Saved "${view.name}".`, { variant: "success" });
  }

  async function saveAsNew(viewName: string) {
    if (!session) return;
    const shown = shownState();
    const started: ShownSnapshot = { activeViewId, shown };
    let answer: TableColumnPreferenceJSON;
    try {
      answer = await createView({ session, tableKey, name: viewName, state: shown }).unwrap();
    } catch (error) {
      showErrorSnackbar(error, SAVE_VIEW_FAILURE);
      return;
    }
    const created = answer.views?.find((view) => view.name.toLowerCase() === viewName.toLowerCase());
    setSaveDialog(0);
    if (created && stillShows(started)) tableViews.keepAsCurrentView(created.id, shown);
    enqueueSnackbar(`Saved "${viewName}".`, { variant: "success" });
  }

  function openSaveDialog() {
    close();
    setSaveDialog((count) => Math.abs(count) + 1);
  }

  function openManage() {
    close();
    setManageOpen(true);
  }

  const items: ReactNode[] = [
    <ListSubheader key="heading">{`${title} views`}</ListSubheader>,
    <ViewItem
      key="default"
      isActive={activeView === undefined}
      edited={activeView === undefined && edited}
      label={DEFAULT_VIEW_NAME}
      secondary="The page's own view"
      onClick={() => choose(null)}
    />,
    ...views.map((view) => (
      <ViewItem
        key={view.id}
        isActive={view.id === activeView?.id}
        edited={view.id === activeView?.id && edited}
        isStarred={view.isStarred}
        label={view.name}
        secondary={view.isStarred ? `Opens when you visit ${title}` : undefined}
        onClick={() => choose(view)}
      />
    )),
    <Divider key="divider" />,
  ];
  if (activeView && edited) {
    items.push(
      <MenuItem key="save" disabled={updateState.isLoading} onClick={() => void saveChanges(activeView)}>
        <ListItemIcon><SaveOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{`Save changes to "${activeView.name}"`}</ListItemText>
      </MenuItem>,
    );
  }
  items.push(
    <MenuItem key="save-as" onClick={openSaveDialog}>
      <ListItemText inset>Save as new view…</ListItemText>
    </MenuItem>,
  );
  if (edited) {
    items.push(
      <MenuItem key="discard" onClick={() => choose(activeView ?? null)}>
        <ListItemText inset>Discard changes</ListItemText>
      </MenuItem>,
    );
  }
  items.push(
    <MenuItem key="manage" disabled={views.length === 0} onClick={openManage}>
      <ListItemText inset secondary="Rename, reorder, delete, choose the starred view">
        Manage views…
      </ListItemText>
    </MenuItem>,
  );

  const label = edited ? `${name} · edited` : name;
  const isOpen = anchor !== null || sheetOpen;

  const starIcon = activeView?.isStarred ? <StarIcon sx={{ color: "warning.main" }} /> : <StarBorderIcon />;
  return (
    <>
      {isPhone ? (
        // On a phone the button is its star alone, with a dot while the view is edited: named in full,
        // it squeezed the search beside it to an icon's width (#6720). The sheet it opens names the view.
        <Button
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={`Views: ${label}`}
          color="inherit"
          onClick={open}
          size="small"
          sx={{ flexShrink: 0, minWidth: 0, px: 1 }}
          variant="outlined"
        >
          <Badge color="primary" variant="dot" invisible={!edited} overlap="circular">
            {starIcon}
          </Badge>
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      ) : (
        <Button
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={`Views: ${label}`}
          color="inherit"
          endIcon={<ArrowDropDownIcon />}
          onClick={open}
          size="small"
          startIcon={starIcon}
          // The wide toolbar row (`ToolbarButtons`) has no gap of its own, so the button keeps the
          // search box off itself there. The narrow row spaces its items with a gap.
          sx={{
            flexShrink: 0,
            maxWidth: { sm: VIEWS_BUTTON_MAX_WIDTH, xs: 168 },
            mr: { md: 2 },
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
          variant="outlined"
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            <Box component="span" sx={{ fontWeight: 500 }}>{name}</Box>
            {edited && (
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}> · edited</Box>
            )}
          </Box>
        </Button>
      )}
      <Menu anchorEl={anchor} open={anchor !== null} onClose={close} slotProps={{ paper: { sx: { maxWidth: 360 } } }}>
        {items}
      </Menu>
      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={close}
        slotProps={{ paper: { "aria-labelledby": sheetTitleId, sx: sheetPaperSx } as object }}
      >
        <SheetHeading id={sheetTitleId} title="Views" onClose={close} />
        <MenuList aria-labelledby={sheetTitleId} autoFocusItem={sheetOpen}>{items}</MenuList>
      </Drawer>
      <SaveViewDialog
        key={saveDialog}
        open={saveDialog > 0}
        saving={createState.isLoading}
        views={views}
        onCancel={() => setSaveDialog((count) => -count)}
        onSave={(viewName) => void saveAsNew(viewName)}
      />
      <ManageViewsDialog open={manageOpen} tableKey={tableKey} title={title} views={views} onClose={() => setManageOpen(false)} />
    </>
  );
}

function ViewItem(
  props: Readonly<{
    isActive: boolean;
    edited: boolean;
    isStarred?: boolean;
    label: string;
    secondary?: string;
    onClick: () => void;
  }>,
) {
  const { isActive, edited, isStarred, label, secondary, onClick } = props;
  return (
    <MenuItem selected={isActive} onClick={onClick}>
      <ListItemIcon>{isActive ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
      <ListItemText
        primary={edited ? `${label} · edited` : label}
        secondary={secondary}
        slotProps={{ primary: { sx: { overflowWrap: "anywhere", whiteSpace: "normal" } } }}
      />
      {isStarred && <StarIcon aria-label="Starred" fontSize="small" sx={{ color: "warning.main", ml: 1 }} />}
    </MenuItem>
  );
}
